/**
 * HUB styles redesign: list content for the anchored 460px Styles panel (sandbox v5).
 * Display-only — DB collection names / kinds untouched.
 */

import { useRef, useState, type ReactNode } from "react";
import { Check, ChevronRight, Heart, LayoutGrid, MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HubCollection, HubStyleCard } from "@/lib/fetchStyles";
import { SignedOutputImage } from "@/features/footwear-studio/SignedOutputImage";
import { StyleActionsMenu } from "@/features/footwear-studio/StyleActionsMenu";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/** CURATION-D1 / DEL-B — Home collection-view card chrome (all optional; default off). */
export type HomeStyleChrome = {
  showVersionCount?: boolean;
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (styleId: string) => void;
  menuCollections?: HubCollection[];
  onMove?: (styleId: string, collectionId: string) => void;
  /** CURATION-D2 — open Home "+ New collection" (create + move this style). */
  onNewCollection?: (styleId: string) => void;
  onRenameRequest?: (styleId: string) => void;
  renamingStyleId?: string | null;
  onRenameCommit?: (styleId: string, name: string) => void;
  onRenameCancel?: () => void;
  /** DEL-B — soft-delete style (opens confirm dialog). */
  onDelete?: (styleId: string) => void;
  writeBusy?: boolean;
};

/** DEL-B — Library card ⋯ (Move / Rename / Delete; no multi-select). */
export type LibraryStyleChrome = {
  menuCollections?: HubCollection[];
  onMove?: (styleId: string, collectionId: string) => void;
  onNewCollection?: (styleId: string) => void;
  onRenameRequest?: (styleId: string) => void;
  renamingStyleId?: string | null;
  onRenameCommit?: (styleId: string, name: string) => void;
  onRenameCancel?: () => void;
  onDelete?: (styleId: string) => void;
  /** Recently deleted pill — Restore only. */
  restoreOnly?: boolean;
  onRestore?: (styleId: string) => void;
  writeBusy?: boolean;
};

type StylePrefetchHandler = (styleId: string) => void;

let hubStylePrefetchHandler: StylePrefetchHandler | null = null;

/** FootwearStudio registers; StudioRailUpload invokes on debounced card hover. */
export function registerHubStylePrefetchHandler(
  handler: StylePrefetchHandler | null,
): void {
  hubStylePrefetchHandler = handler;
}

export function invokeHubStylePrefetch(styleId: string): void {
  hubStylePrefetchHandler?.(styleId);
}

/** Overlay designs view — shared typography (strip + rows). */
export const OVERLAY_MICRO_LABEL_CLASS =
  "text-[9px] font-semibold uppercase tracking-[0.12em] text-textMuted leading-none";

export const OVERLAY_ROW_HEIGHT_PX = 74;
export const OVERLAY_VISIBLE_ROWS = 4;
export const OVERLAY_LIST_MAX_HEIGHT_PX =
  OVERLAY_ROW_HEIGHT_PX * OVERLAY_VISIBLE_ROWS;

/** Overlay row grid: thumb | name | type | collection | edited | versions | stages | chevron */
export const OVERLAY_ROW_GRID_CLASS =
  "grid-cols-[50px_165px_120px_125px_105px_minmax(150px,1fr)_140px_20px]";

/** Default / untitled collection → "My Styles" (display only). */
export function displayCollectionName(collection: HubCollection): string {
  const raw = collection.name?.trim() ?? "";
  if (!raw || /^untitled(\s+collection)?$/i.test(raw)) {
    return "My Styles";
  }
  return raw;
}

export function collectionSeasonTag(collection: HubCollection): string | null {
  const parts = [
    collection.season,
    collection.quarter,
    collection.year != null ? String(collection.year) : null,
  ].filter((p): p is string => typeof p === "string" && p.trim().length > 0);
  if (parts.length === 0) return null;
  return parts.join(" ");
}

function styleLabel(style: HubStyleCard): string {
  return (
    style.name?.trim() ||
    (style.style_number != null
      ? `Style ${style.style_number}`
      : "Untitled style")
  );
}

function formatRelativeActivity(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) {
    return mins === 1 ? "Updated 1 minute ago" : `Updated ${mins} minutes ago`;
  }
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return hours === 1 ? "Updated 1 hour ago" : `Updated ${hours} hours ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return days === 1 ? "Updated 1 day ago" : `Updated ${days} days ago`;
  }
  const weeks = Math.floor(days / 7);
  if (weeks < 5) {
    return weeks === 1 ? "Updated 1 week ago" : `Updated ${weeks} weeks ago`;
  }
  const months = Math.floor(days / 30);
  return months === 1 ? "Updated 1 month ago" : `Updated ${months} months ago`;
}

function displayValue(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "—";
}

function MetaColumn({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={cn("shrink-0 flex flex-col justify-center gap-0.5 min-w-0", className)}>
      <span className={OVERLAY_MICRO_LABEL_CLASS}>{label}</span>
      <span className="text-[13px] text-textSecondary truncate leading-tight">{value}</span>
    </div>
  );
}

function OverlayStageChips({ style }: { style: HubStyleCard }) {
  const stages = [
    { key: "MV", active: !!style.thumb_url },
    { key: "MA", active: style.has_angles },
    { key: "OB", active: style.has_orbit },
  ] as const;

  return (
    <div className="shrink-0 w-full flex flex-col justify-center">
      <span className={cn(OVERLAY_MICRO_LABEL_CLASS, "mb-1")}>Stages</span>
      <div className="flex items-center gap-1.5 flex-nowrap">
        {stages.map((s) => (
          <span
            key={s.key}
            className={cn(
              "text-[11px] font-semibold rounded-[6px] px-[9px] py-[3px] leading-none shrink-0",
              s.active
                ? "text-[#0a0d12] bg-[#5cc8f5]"
                : "text-textFaint bg-transparent border border-white/[0.12]",
            )}
          >
            {s.key}
          </span>
        ))}
      </div>
    </div>
  );
}

export function OverlayStageDots({ style }: { style: HubStyleCard }) {
  const stages = [
    { key: "MV", active: !!style.thumb_url },
    { key: "MA", active: style.has_angles },
    { key: "OB", active: style.has_orbit },
  ] as const;
  return (
    <span className="flex items-center gap-[3px] shrink-0" aria-label="Stages">
      {stages.map((s) => (
        <span
          key={s.key}
          title={s.key}
          className={cn(
            "w-[8px] h-[8px] rounded-[2px]",
            s.active ? "bg-[#5cc8f5]" : "bg-white/[0.14]",
          )}
        />
      ))}
    </span>
  );
}

function OverlayStyleCard({
  style,
  isLoaded,
  onSelect,
  onHoverStyle,
  cardExtra,
  homeChrome,
  libraryChrome,
}: {
  style: HubStyleCard;
  isLoaded: boolean;
  onSelect?: (styleId: string) => void;
  onHoverStyle?: (styleId: string) => void;
  cardExtra?: (style: HubStyleCard) => ReactNode;
  homeChrome?: HomeStyleChrome | null;
  libraryChrome?: LibraryStyleChrome | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const skipRenameBlurRef = useRef(false);
  const chrome = homeChrome ?? null;
  const lib = libraryChrome ?? null;
  const selected = !!chrome?.selectedIds?.has(style.id);
  const anySelected = (chrome?.selectedIds?.size ?? 0) > 0;
  const showSelectChrome = !!chrome?.onToggleSelect;
  const showSelectBox = showSelectChrome && (hovered || anySelected || selected);
  const menuCollections =
    chrome?.menuCollections ?? lib?.menuCollections ?? null;
  const showMenu =
    !!menuCollections &&
    (menuCollections.length > 0 || lib?.restoreOnly === true);
  const showMenuBtn = showMenu && (hovered || menuOpen);
  const isHidden = style.hidden === true;
  const label = styleLabel(style);
  const legacyVersionCount = (style.variant_thumb_urls ?? []).length;
  const vc = style.version_count;
  const versionBadge =
    chrome?.showVersionCount
      ? vc != null && vc > 0
        ? vc === 1
          ? "1 version"
          : `${vc} versions`
        : null
      : legacyVersionCount > 0
        ? `v${legacyVersionCount}`
        : null;
  const extra = cardExtra?.(style) ?? null;
  const isRenaming =
    chrome?.renamingStyleId === style.id ||
    lib?.renamingStyleId === style.id;
  const renameCommit = chrome?.onRenameCommit ?? lib?.onRenameCommit;
  const renameCancel = chrome?.onRenameCancel ?? lib?.onRenameCancel;
  const writeBusy = chrome?.writeBusy ?? lib?.writeBusy;

  const openStyle = () => {
    if (!onSelect) return;
    if (anySelected && chrome?.onToggleSelect) {
      chrome.onToggleSelect(style.id);
      return;
    }
    onSelect(style.id);
  };

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? openStyle : undefined}
      onMouseEnter={() => {
        setHovered(true);
        onHoverStyle?.(style.id);
      }}
      onMouseLeave={() => setHovered(false)}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openStyle();
              }
            }
          : undefined
      }
      className={cn(
        "group flex flex-col rounded-[9px] overflow-hidden min-w-0 transition-[border-color,box-shadow,background-color,opacity]",
        "border border-white/[0.08] bg-bgNested",
        onSelect &&
          "cursor-pointer hover:border-[1.5px] hover:border-[#38bdf8] hover:shadow-[0_0_0_3px_rgba(56,189,248,0.15)]",
        isLoaded &&
          "border-[1.5px] border-[#38bdf8] bg-[rgba(56,189,248,0.05)] shadow-[0_0_0_3px_rgba(56,189,248,0.15)]",
        isHidden && (chrome || lib?.restoreOnly) && "opacity-45",
        selected && "border-[1.5px] border-[#38bdf8]",
      )}
    >
      <div className="relative w-full aspect-[4/3] shrink-0 bg-bgNested">
        {style.thumb_url ? (
          <SignedOutputImage
            storageRef={style.thumb_url}
            alt={label}
            className="w-full h-full object-contain"
            hubThumb
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-textFaint">
            <LayoutGrid className="h-4 w-4" />
          </div>
        )}
        {showSelectBox ? (
          <button
            type="button"
            aria-label={selected ? "Deselect style" : "Select style"}
            disabled={!!writeBusy}
            onClick={(e) => {
              e.stopPropagation();
              chrome?.onToggleSelect?.(style.id);
            }}
            className={cn(
              "absolute top-1 left-1 z-[2] flex h-5 w-5 items-center justify-center rounded-[6px] border",
              selected
                ? "border-[#38bdf8] bg-[#38bdf8]"
                : "border-white/10 bg-[rgba(17,19,24,0.8)]",
            )}
          >
            {selected ? (
              <Check className="h-3 w-3 text-[#04121d]" strokeWidth={3} />
            ) : null}
          </button>
        ) : null}
        {style.has_favourite ? (
          <span
            title="Has favourites"
            aria-hidden
            className={cn(
              "pointer-events-none absolute top-1 z-[1] flex h-[18px] w-[18px] items-center justify-center rounded-[5px] bg-[rgba(17,19,24,0.78)]",
              showSelectBox ? "left-[28px]" : "left-1",
            )}
          >
            <Heart
              className="h-[11px] w-[11px]"
              fill="#fb7185"
              stroke="#fb7185"
            />
          </span>
        ) : null}
        {isLoaded && (
          <span
            className={cn(
              "absolute top-1 rounded-full bg-emerald-500/90 px-[5px] py-[1px] text-[8px] font-semibold uppercase tracking-[0.04em] text-[#04342c]",
              showSelectChrome ? "left-7" : "left-1",
            )}
          >
            Loaded
          </span>
        )}
        {showMenuBtn ? (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Style actions"
                disabled={!!writeBusy}
                onClick={(e) => e.stopPropagation()}
                className="absolute top-1 right-1 z-[2] flex h-6 w-6 items-center justify-center rounded-[8px] border border-white/10 bg-[rgba(17,19,24,0.8)] text-white hover:border-[#38bdf8] hover:text-[#38bdf8]"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <StyleActionsMenu
              collections={menuCollections ?? []}
              currentCollectionId={style.collection_id}
              writeBusy={writeBusy}
              restoreOnly={lib?.restoreOnly === true}
              onRestore={
                lib?.onRestore ? () => lib.onRestore?.(style.id) : undefined
              }
              onMove={(collectionId) => {
                (chrome?.onMove ?? lib?.onMove)?.(style.id, collectionId);
              }}
              onNewCollection={
                chrome?.onNewCollection || lib?.onNewCollection
                  ? () =>
                      (chrome?.onNewCollection ?? lib?.onNewCollection)?.(
                        style.id,
                      )
                  : undefined
              }
              onRename={
                chrome?.onRenameRequest || lib?.onRenameRequest
                  ? () =>
                      (chrome?.onRenameRequest ?? lib?.onRenameRequest)?.(
                        style.id,
                      )
                  : undefined
              }
              onDelete={
                chrome?.onDelete || lib?.onDelete
                  ? () => (chrome?.onDelete ?? lib?.onDelete)?.(style.id)
                  : undefined
              }
              onContentClick={(e) => e.stopPropagation()}
            />
          </DropdownMenu>
        ) : null}
        {versionBadge ? (
          <span className="absolute bottom-1 right-1 rounded-[4px] bg-[rgba(17,19,24,0.8)] px-[5px] py-[1px] font-mono text-[8px] text-textSecondary">
            {versionBadge}
          </span>
        ) : null}
      </div>
      <div className="shrink-0 px-[7px] py-[5px]">
        {isRenaming ? (
          <input
            autoFocus
            defaultValue={style.name?.trim() ?? ""}
            disabled={!!writeBusy}
            className="w-full rounded border border-[#38bdf8] bg-[#161616] px-1 text-[11.5px] font-medium text-white outline-none"
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                e.preventDefault();
                skipRenameBlurRef.current = true;
                renameCancel?.();
              } else if (e.key === "Enter") {
                e.preventDefault();
                renameCommit?.(
                  style.id,
                  (e.target as HTMLInputElement).value,
                );
              }
            }}
            onBlur={(e) => {
              if (skipRenameBlurRef.current) {
                skipRenameBlurRef.current = false;
                return;
              }
              renameCommit?.(style.id, e.target.value);
            }}
          />
        ) : (
          <div className="truncate text-[11.5px] font-medium leading-tight text-white">
            {label}
          </div>
        )}
        <div className="mt-[2px] flex items-center gap-1">
          <span className="min-w-0 flex-1 truncate text-[8.5px] leading-tight text-textMuted">
            {displayValue(style.shoe_type)} · {formatRelativeActivity(style.last_activity_at)}
          </span>
          <OverlayStageDots style={style} />
        </div>
        {extra ? <div className="mt-1">{extra}</div> : null}
      </div>
    </div>
  );
}

function OverlayFannedDeck({ urls }: { urls: string[] }) {
  if (urls.length === 0) return null;

  return (
    <div className="flex items-center" style={{ height: 42 }} aria-hidden>
      {urls.map((url, i) => (
        <div
          key={`${url}-${i}`}
          className="relative overflow-hidden rounded-[8px] border-[1.5px] border-bgPage bg-bgNested"
          style={{
            width: 42,
            height: 42,
            marginLeft: i === 0 ? 0 : -24,
            zIndex: i + 1,
            boxShadow:
              i > 0 ? "-2px 0 6px rgba(0,0,0,0.45)" : undefined,
          }}
        >
          <SignedOutputImage
            storageRef={url}
            alt=""
            className="w-full h-full object-contain"
          />
        </div>
      ))}
    </div>
  );
}

function OverlayStyleRow({
  style,
  isLoaded,
  onSelect,
}: {
  style: HubStyleCard;
  isLoaded: boolean;
  onSelect?: (styleId: string) => void;
}) {
  const label = styleLabel(style);
  const deckUrls = style.variant_thumb_urls ?? [];
  const versionCount = deckUrls.length;

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? () => onSelect(style.id) : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(style.id);
              }
            }
          : undefined
      }
      className={cn(
        "grid items-center gap-4 py-[11px] pl-[22px] pr-[22px] h-[74px] w-full min-w-0 text-left transition-colors border-t border-white/[0.05]",
        OVERLAY_ROW_GRID_CLASS,
        onSelect && "cursor-pointer hover:bg-white/[0.03]",
        isLoaded && "bg-[rgba(56,189,248,0.05)]"
      )}
    >
      <div
        className="w-[50px] h-[50px] rounded-[9px] overflow-hidden shrink-0 border border-white/10"
        style={{ background: "var(--bg-nested)" }}
      >
        {style.thumb_url ? (
          <SignedOutputImage
            storageRef={style.thumb_url}
            alt={label}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-textFaint">
            <LayoutGrid className="h-4 w-4" />
          </div>
        )}
      </div>

      <div className="shrink-0 w-full min-w-0 flex flex-col justify-center gap-0.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[15px] font-semibold text-white truncate leading-tight">
            {label}
          </span>
          {isLoaded && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[12px] font-medium text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Loaded
            </span>
          )}
        </div>
        {style.style_number != null && (
          <p className="text-[12px] text-textMuted font-mono truncate leading-tight">
            #{style.style_number}
          </p>
        )}
      </div>

      <MetaColumn
        label="Type"
        value={displayValue(style.shoe_type)}
        className="w-full"
      />
      <MetaColumn
        label="Collection"
        value={displayValue(style.collection_name)}
        className="w-full"
      />
      <MetaColumn
        label="Edited"
        value={formatRelativeActivity(style.last_activity_at)}
        className="w-full"
      />

      <div className="min-w-0 overflow-hidden flex flex-col justify-center gap-1">
        <span className={cn(OVERLAY_MICRO_LABEL_CLASS, "truncate")}>
          {versionCount} {versionCount === 1 ? "Version" : "Versions"}
        </span>
        <div className="min-w-0 overflow-hidden">
          <OverlayFannedDeck urls={deckUrls} />
        </div>
      </div>

      <OverlayStageChips style={style} />
      <ChevronRight className="h-5 w-5 text-textFaint shrink-0 justify-self-end" />
    </div>
  );
}

function DefaultStyleRow({
  style,
  isLoaded,
  onSelect,
}: {
  style: HubStyleCard;
  isLoaded: boolean;
  onSelect?: (styleId: string) => void;
}) {
  const label = styleLabel(style);

  return (
    <div
      role={onSelect ? "button" : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={onSelect ? () => onSelect(style.id) : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(style.id);
              }
            }
          : undefined
      }
      className={cn(
        "relative flex items-center gap-3 px-3 py-2.5 w-full text-left transition-colors border-t border-white/[0.05]",
        onSelect && "cursor-pointer hover:bg-white/[0.03]",
        isLoaded && "bg-[rgba(56,189,248,0.05)]"
      )}
    >
      {isLoaded && (
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px] bg-sky-400"
          aria-hidden
        />
      )}

      <div
        className="w-12 h-12 rounded-lg overflow-hidden shrink-0 border border-white/10"
        style={{ background: "var(--bg-nested)" }}
      >
        {style.thumb_url ? (
          <SignedOutputImage
            storageRef={style.thumb_url}
            alt={label}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-textFaint">
            <LayoutGrid className="h-4 w-4" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[14px] font-medium text-white truncate leading-tight">
            {label}
          </span>
          {isLoaded && (
            <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium text-emerald-400 bg-emerald-500/[0.13]">
              Loaded
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[11px] font-medium">
          {style.style_number != null && (
            <span className="text-textMuted font-mono">#{style.style_number}</span>
          )}
          <span
            className={
              style.thumb_url ? "text-[#38bdf8]" : "text-textFaint"
            }
          >
            MV
          </span>
          <span className="text-textFaint">·</span>
          <span
            className={
              style.has_angles ? "text-[#38bdf8]" : "text-textFaint"
            }
          >
            MA
          </span>
          <span className="text-textFaint">·</span>
          <span
            className={style.has_orbit ? "text-[#38bdf8]" : "text-textFaint"}
          >
            OB
          </span>
        </div>
      </div>

      <Heart
        className={cn(
          "h-3.5 w-3.5 shrink-0",
          style.has_favourite ? "text-[#fb7185]" : "text-textFaint"
        )}
        fill={style.has_favourite ? "#fb7185" : "none"}
        stroke={style.has_favourite ? "#fb7185" : "currentColor"}
        aria-label={style.has_favourite ? "Has favourites" : "No favourites"}
      />
      <ChevronRight className="h-4 w-4 text-textFaint shrink-0" />
    </div>
  );
}

function StyleRow({
  style,
  isLoaded,
  onSelect,
  rowLayout = "default",
}: {
  style: HubStyleCard;
  isLoaded: boolean;
  onSelect?: (styleId: string) => void;
  rowLayout?: "default" | "overlay";
}) {
  if (rowLayout === "overlay") {
    return (
      <OverlayStyleRow style={style} isLoaded={isLoaded} onSelect={onSelect} />
    );
  }
  return (
    <DefaultStyleRow style={style} isLoaded={isLoaded} onSelect={onSelect} />
  );
}

export function HubStylesPanelList({
  collections,
  activeStyleId,
  onSelectStyle,
  onHoverStyle,
  emptyMessage = "No styles yet",
  rowLayout = "default",
  columns = 5,
  hideDefaultCollectionLabel = false,
  cardExtra,
  homeChrome = null,
  libraryChrome = null,
}: {
  collections: HubCollection[];
  activeStyleId?: string | null;
  onSelectStyle?: (styleId: string) => void;
  onHoverStyle?: (styleId: string) => void;
  emptyMessage?: string;
  /** Overlay upload panel uses fanned deck + truthful stage dots; library page uses grouped card grid. */
  rowLayout?: "default" | "overlay" | "library";
  /** Card grid column count at xl breakpoint (library layout only). */
  columns?: number;
  /** Studio Home: omit the default "My Styles" collection header above the grid. */
  hideDefaultCollectionLabel?: boolean;
  /** Optional line under each library/overlay card (e.g. Artwork status). */
  cardExtra?: (style: HubStyleCard) => ReactNode;
  /** CURATION-D1 — Home-only selection / ⋯ / version_count chrome (default off). */
  homeChrome?: HomeStyleChrome | null;
  /** DEL-B — Library card ⋯ (Move / Rename / Delete); keep homeChrome unset in Library. */
  libraryChrome?: LibraryStyleChrome | null;
}) {
  const libraryGridClass =
    columns >= 6
      ? "grid grid-cols-2 gap-[9px] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
      : columns <= 4
        ? "grid grid-cols-2 gap-[9px] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4"
        : "grid grid-cols-2 gap-[9px] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

  if (collections.length === 0) {
    return (
      <div
        className={cn(
          "py-8 text-center",
          rowLayout === "overlay" ? "px-[22px]" : "px-3",
        )}
      >
        <p
          className={cn(
            rowLayout === "overlay"
              ? "text-[12px] font-medium text-textMuted"
              : "text-[11px] text-textFaint",
          )}
        >
          {emptyMessage}
        </p>
      </div>
    );
  }

  if (rowLayout === "library") {
    const hasStyles = collections.some((c) => c.styles.length > 0);
    if (!hasStyles) {
      return (
        <div className="px-3 py-8 text-center">
          <p className="text-[12px] font-medium text-textMuted">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="pb-6">
        {collections.map((collection) => {
          if (collection.styles.length === 0) return null;
          const title = displayCollectionName(collection);
          const season = collectionSeasonTag(collection);
          const isDefault = title === "My Styles";
          const showCollectionHeader = !(hideDefaultCollectionLabel && isDefault);
          return (
            <section
              key={collection.id}
              className={cn(showCollectionHeader ? "mb-6 last:mb-0" : "mb-0")}
            >
              {showCollectionHeader ? (
                <div className="sticky top-0 z-[1] border-b border-white/[0.06] bg-bgPage/95 px-1 pb-2 pt-1 backdrop-blur-sm">
                  <div className="flex min-w-0 items-baseline gap-2">
                    <p className="truncate text-[11px] font-medium uppercase tracking-wide text-textMuted">
                      {title}
                    </p>
                    {!isDefault && season ? (
                      <span className="shrink-0 truncate text-[10px] font-medium text-sky-400/90">
                        {season}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <div className={cn(showCollectionHeader && "pt-3", libraryGridClass)}>
                {collection.styles.map((style) => (
                  <OverlayStyleCard
                    key={style.id}
                    style={style}
                    isLoaded={!!activeStyleId && style.id === activeStyleId}
                    onSelect={onSelectStyle}
                    onHoverStyle={onHoverStyle}
                    cardExtra={cardExtra}
                    homeChrome={homeChrome}
                    libraryChrome={libraryChrome}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  if (rowLayout === "overlay") {
    const flatStyles = collections.flatMap((c) => c.styles);
    if (flatStyles.length === 0) {
      return (
        <div className="px-[22px] py-8 text-center">
          <p className="text-[12px] font-medium text-textMuted">{emptyMessage}</p>
        </div>
      );
    }
    return (
      <div className="px-[22px] py-[10px]">
        <div className="grid grid-cols-2 gap-[9px] xl:grid-cols-4">
          {flatStyles.map((style) => (
            <OverlayStyleCard
              key={style.id}
              style={style}
              isLoaded={!!activeStyleId && style.id === activeStyleId}
              onSelect={onSelectStyle}
              onHoverStyle={onHoverStyle}
              homeChrome={homeChrome}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {collections.map((collection) => {
        const title = displayCollectionName(collection);
        const season = collectionSeasonTag(collection);
        const isDefault = title === "My Styles";
        return (
          <div key={collection.id}>
            <div className="px-3 pt-3 pb-1.5 sticky top-0 z-[1]" style={{ background: "var(--bg-surface)" }}>
              <div className="flex items-baseline gap-2 min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-wide text-textMuted truncate">
                  {title}
                </p>
                {!isDefault && season && (
                  <span className="text-[10px] font-medium text-sky-400 truncate shrink-0">
                    {season}
                  </span>
                )}
              </div>
            </div>
            {collection.styles.map((style) => (
              <StyleRow
                key={style.id}
                style={style}
                isLoaded={!!activeStyleId && style.id === activeStyleId}
                onSelect={onSelectStyle}
                rowLayout={rowLayout}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
